import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Route, Router, UrlSegment, UrlTree } from "@angular/router";
import { GigyaService2 } from "../gigya-service2";
import { CacheService } from "../cache-service";
import { CACHE_KEYS } from "../../constants/cache-keys";
import { ReusableService } from "../reusable-services";
import { SrpContext, UserContextResponse } from "../../../header/user-profile/user-profile.model";
import { SettingsService } from "../settings-service";
import { LogoutService } from "../logout-service";
import { LoginService } from "../../../login/login-service";
import { SRP_ERRORS } from "../../constants/srp-errors";
import { LoginSetSessionRequest } from "../../../login/login-session-model";
import { SRP_ROUTES } from "../../constants/routes";
import { hasCookie } from "../../utils/utils";
import { COOKIE_CONSTANTS } from "../../constants/cookie";
import { eGigyaScript } from "../../constants/gigya-script-types";

type AuthMode = 'required' | 'avoid' | 'public' | 'partial';

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly gigyaService2 = inject(GigyaService2);
  private readonly cacheService = inject(CacheService);
  private readonly reusableService = inject(ReusableService);
  private readonly settingsService = inject(SettingsService);
  private readonly logoutService = inject(LogoutService);
  private readonly loginService = inject(LoginService);


  // ---------- CanActivate for component routes ----------
  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const token = route.queryParamMap.get('token'); //this is present only on first login or SSO redirect
    const sessionType = route.queryParamMap.get('sso'); //this is present only on first login or SSO redirect
    const mode = route.queryParamMap.get('mode');
    const spName = route.queryParamMap.get('spName');

    const routePath = route.pathFromRoot.map(r => r.routeConfig?.path ?? '').filter(Boolean).join('/');
    console.log('AuthGuard: canActivate called for path:', routePath, 'token:', token, 'sessionType:', sessionType);

    // Allow the SAML bootstrap route to render and execute its redirect/init script.
    if (this.isSamlInitRoute(routePath, mode, spName)) {
      return true;
    }
    
    const forceResult = await this.handleForceFlags(route, routePath);
    if (forceResult !== null) {
      return forceResult;
    }

    const authMode = (route.data?.['auth'] as AuthMode) ?? 'public';
    const active = await this.isLoggedIn(token, sessionType);
    let canActivate = this.settingsService.isLocal() || this.decide(active, authMode);

    if (canActivate === true && authMode == 'required') {
      canActivate = await this.loadUserContext(canActivate, routePath);
    } else {
      console.log('Authguard else block hit. canActivate:', canActivate, 'mode:', authMode, 'route:', routePath);
    }
    return canActivate;
  }

  private isSamlInitRoute(routePath: string, mode: string | null, spName: string | null): boolean {
    return routePath === SRP_ROUTES.LOGIN_SAML_PROXY.PATH && mode === 'initSSO' && !!spName;
  }

  private async handleForceFlags(route: ActivatedRouteSnapshot, routePath: string): Promise<boolean | UrlTree | null> {
    if (route.pathFromRoot.some(r => this.isLoginRoute(r.routeConfig?.path))) {
      const resetPassword = route.queryParamMap.get('resetpassword');
      if (this.shouldAllowLoginRouteAccess(resetPassword)) {
        return true;
      }
    } else {
      const forceLogout = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.FORCE_LOGOUT);
      if (forceLogout) {
        this.cacheService.removeFromCache(CACHE_KEYS.LOGIN.FORCE_LOGOUT);
        console.log('Force logout flag found. Logging out user and redirecting to login page.', 'route:', routePath);
        return await this.logoutAndRedirectToLogin(routePath);
      }
    }
    return null;
  }

  private async loadUserContext(canActivate: boolean | UrlTree, routePath: string): Promise<boolean | UrlTree> {
    if (this.reusableService.getSrpContextVariable()) {
      return canActivate;
    }
    try {
      const userContextObservable: UserContextResponse = await this.loginService.getUserContext();
      const srpContext: SrpContext = userContextObservable?.data?.srpContext;
      if (!srpContext) {
        this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_ERROR, SRP_ERRORS.LOGIN_ERRORS.GRAPHQL_FAILED);
        console.error("Error fetching user context fromAPI in server, redirecting to login page!", 'route:', routePath);
        return await this.logoutAndRedirectToLogin(routePath);
      }
      this.reusableService.setUserContextVariable(srpContext);
      this.reusableService.setUserDetails(srpContext);
      return canActivate;
    } catch (e) {
      console.error("Error fetching user context from Server, redirecting to login page!", 'route:', routePath);
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_ERROR, SRP_ERRORS.LOGIN_ERRORS.GRAPHQL_FAILED);
      return await this.logoutAndRedirectToLogin(routePath);
    }
  }


  async logoutAndRedirectToLogin(routePath: string, userAlreadyLoggedOut: boolean = false): Promise<boolean | UrlTree> {
    let canActivate: boolean | UrlTree = true;
    if (!userAlreadyLoggedOut) {
      try {
        await this.logoutService.logout();
        canActivate = this.router.parseUrl('/login');
      }
      catch (e) {
        this.cacheService.addToCache(CACHE_KEYS.LOGIN.FORCE_LOGIN, true);
        console.error("Error during logout, redirecting to login page!", 'route:', routePath, 'error:', e);
        canActivate = this.router.parseUrl('/login');
      }
    }
    return canActivate;
  }

  // ---------- CanMatch for path-matching (useful on /login) ----------
  async canMatch(route: Route, _segments: UrlSegment[]): Promise<boolean | UrlTree> {
    if (this.isLoginRoute(route.path)) {
      const currentNavigation = this.router.currentNavigation();
      const targetUrlTree = currentNavigation?.finalUrl ?? currentNavigation?.extractedUrl;
      const resetPassword = targetUrlTree?.queryParams?.['resetpassword']
        ?? this.router.parseUrl(this.router.url).queryParams?.['resetpassword']
        ?? null;

      const samlMode = targetUrlTree?.queryParams?.['saml'];
        
      if (this.shouldAllowLoginRouteAccess(resetPassword, samlMode)) {
        return true;
      }
    }

    const mode = (route.data?.['auth'] as AuthMode) ?? 'public';
    const active = await this.isLoggedIn();
    return this.decide(active, mode);
  }

  private isLoginRoute(path?: string): boolean {
    return path === SRP_ROUTES.LOGIN.PATH;
  }

  private shouldAllowLoginRouteAccess(resetPassword: string | null, samlMode?: string | null): boolean {
    if (resetPassword) {
      console.log('Reset password flow detected. Allowing access to login page.');
      return true;
    }

    if (samlMode) {
      console.log('SAML mode detected. Allowing access to login page.');
      return true;
    } 

    const forceLogin = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.FORCE_LOGIN);
    if (forceLogin) {
      this.cacheService.removeFromCache(CACHE_KEYS.LOGIN.FORCE_LOGIN);
      return true;
    }

    return false;
  }

  // ---------- Shared decision logic ----------
  private decide(active: boolean, mode: AuthMode): boolean | UrlTree {
    switch (mode) {
      case 'required': // will retrieve context in next step.
      case 'partial': // check active session but context is not needed.
        // Page needs an active session
        return active ? true : this.router.parseUrl('/login');
      case 'avoid': // will not retrieve context, will retrive context when landing page is retrieved.
        // Auth page (e.g., /login); if already logged in, send to landing
        return active ? this.router.parseUrl('/landingpage') : true;

      case 'public':
      default:
        // No check: allow either way
        return true;
    }
  }

  isSRPSessionCookiePresent(): boolean {
    return hasCookie(COOKIE_CONSTANTS.SRP_SESSION);
  }

  async isLoggedIn(token?: string | null, sessionType?: string | null): Promise<boolean> {
    if (token && sessionType) { //this is present only on first login or SSO redirect
      const headers = {
        Authorization: `Bearer ${token}`,
        "x-login-type": 'sso'
      }

      return new Promise<boolean>((resolve) => {
        this.loginService.login(headers).then(() => {

          //Set session type in cache
          this.cacheService.addToCache(CACHE_KEYS.LOGIN.SESSION_TYPE, 'sso');

          console.log("SRP SSO Session established successfully");
          resolve(true);
        }).catch((err: any) => {
          console.error(" Login Error:", err);
          resolve(false);
        });
      });

    }
    else {
      let isSRPSessionCookiePresent = this.isSRPSessionCookiePresent();

      let isKeepMeLoggedInChecked = this.cacheService.getFromCache(CACHE_KEYS.USER.KEEP_ME_LOGGED_IN);
      if (isSRPSessionCookiePresent) {
        return true;
      }

      await this.gigyaService2.loadGigyaScripts(eGigyaScript.MAIN);

      return new Promise<boolean>((resolve) => {
        (window as any).gigya.accounts.getAccountInfo({
          callback: async (res: any) => {
            console.log("Gigya getAccountInfo response:", res?.errorCode);
            if (res?.errorCode === 0) {
              if (isKeepMeLoggedInChecked) {
                //call gigya jwt then IL login endpoint to re-establish SRP session
                console.log("Re-establishing SRP session from Gigya session");
                this.gigyaService2.getJWT()
                  .then((token: string) => this.reEstablishSrpSession(token, res.UID, resolve));
              } else if (!isKeepMeLoggedInChecked) {
                console.log("Keep Me Logged In not checked");
                //logout gigya session call logout method clear cookie and local storage and redirect to login
                try {
                  await this.logoutService.logout();
                } catch (error) {

                }
                resolve(false);
              } else {
                console.log("Active SRP and Gigya session found");
                resolve(true);
              }
            }
            else {
              console.log("No active Gigya session found");
              resolve(false)
            }
          },
        });
      });

    }

  }


  private reEstablishSrpSession(token: string, uid: string, resolve: (value: boolean) => void): void {
    const headers = { Authorization: `Bearer ${token}` };
    const request: LoginSetSessionRequest = { UID: uid };
    this.loginService.login(headers, request).then(() => {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.SESSION_TYPE, 'gigya');
      console.log("SRP session re-established successfully");
      resolve(true);
    }).catch((err: any) => {
      console.error(" Login Error:", err);
      resolve(false);
    });
  }

  removeGigyaScript() {
    const script = document.getElementById("srp-gigya-script");
    if (script) script.remove();
  }
}